<template>
  <div data-test-id="PricingComparison__container">
    <ParagraphBody
      v-if="showTagline"
      bodySize="large"
      data-test-id="PricingComparison__tagLine"
    >
      Based on your package you've consolidated {{ vendorsConsolidated }}
      vendors and saved ${{ savingsPerUser }} per user, for a total savings
      of <strong>${{ savingsTotal }}</strong>.
    </ParagraphBody>
    <div :class="[$style.graphBorder, $style.comparisonContainer]">
      <div :class="$style.barGraphContainer">
        <span data-test-id="PricingComparison__jcMonthlyCost">
          {{ jcMonthlyRateDisplay }}/mo
        </span>
        <BarGraph
          :columnHeight="jcMonthlyRate"
          :columnHeightMax="columnHeightMax"
          :columnSlices="[planName]"
          data-test-id="PricingComparison__jcBarchart"
          :isJcColumn="true"
          :usersCount="usersCount"
        />
      </div>
      <div :class="$style.barGraphContainer">
        <span data-test-id="PricingComparison__competitorMonthlyCost">
          ${{ competitiveComparisonMonthlyCost.toLocaleString() }}/mo
        </span>
        <BarGraph
          :columnHeight="competitiveComparisonMonthlyCost"
          :columnHeightMax="columnHeightMax"
          :columnSlices="competitiveComparisonFeatures"
          data-test-id="PricingComparison__competitorBarchart"
          :startColumnSliceColor="116"
          :usersCount="usersCount"
        />
      </div>
    </div>
    <div :class="[$style.comparisonContainer, $style.graphLabelContainer]">
      <JumpcloudBrandmark
        :class="$style.graphLabel"
        :scale="1"
        type="jcLogoWordmark"
      />
      <span :class="$style.graphLabel">
        <strong>mixed solutions</strong>
      </span>
    </div>
  </div>
</template>

<script>
import { JumpcloudBrandmark, ParagraphBody } from '@jumpcloud/ui-components';
import BarGraph from 'client/components/Graphs/BarGraph.vue';
import CheckoutUtil from 'client/GrowthEngineering/utilities/CheckoutUtil';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';

const {
  ProductCodeToSubscriptionType,
  CompetitiveComparisonFeaturesByPackage,
} = SubscriptionConstants;

export default {
  name: 'PricingComparison',

  components: {
    BarGraph,
    JumpcloudBrandmark,
    ParagraphBody,
  },

  CompetitiveComparisonFeaturesByPackage,
  ProductCodeToSubscriptionType,

  props: {
    planMonthlyPrice: {
      required: true,
      type: Number,
    },
    planName: {
      required: true,
      type: String,
    },
    productCode: {
      required: true,
      type: String,
    },
    showTagline: {
      default: false,
      type: Boolean,
    },
    usersCount: {
      required: true,
      default: 0,
      type: Number,
    },
  },

  computed: {
    columnHeightMax() {
      return (this.competitiveComparisonMonthlyCost * 0.1) + this.competitiveComparisonMonthlyCost;
    },

    competitiveComparisonFeatures() {
      const subscriptionType = this.$options.ProductCodeToSubscriptionType[this.productCode];
      const competitiveFeatureList = this.$options
        .CompetitiveComparisonFeaturesByPackage[subscriptionType];
      return competitiveFeatureList;
    },

    competitiveComparisonMonthlyCost() {
      let totalCost = 0;
      // account for if user responds with no to each question
      if (this.competitiveComparisonFeatures) {
        this.competitiveComparisonFeatures.forEach((feature) => {
          totalCost
            += feature.price * this.usersCount;
        });
      }
      return totalCost % 1 !== 0 ? totalCost.toFixed(2) : totalCost;
    },

    jcMonthlyRate() {
      if (!this.planMonthlyPrice || this.usersCount <= 10) {
        return 0;
      }

      return this.planMonthlyPrice * this.usersCount;
    },

    jcMonthlyRateDisplay() {
      return CheckoutUtil.formatDisplayPrice(this.jcMonthlyRate);
    },

    savingsPerUser() {
      return (this.competitiveComparisonMonthlyCost - this.jcMonthlyRate) / this.usersCount;
    },

    savingsTotal() {
      return (this.competitiveComparisonMonthlyCost - this.jcMonthlyRate);
    },

    vendorsConsolidated() {
      let vendorsConsolidated = 0;
      if (this.competitiveComparisonFeatures) {
        vendorsConsolidated = this.competitiveComparisonFeatures.length;
      }

      return vendorsConsolidated;
    },
  },
};
</script>

<style module>
.barGraphContainer {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0 var(--jc-spacer-large);
  width: 100%;
}

.graphBorder {
  border: 0.2rem solid var(--jc-border-color);
  border-radius: var(--jc-border-radius-large);
  height: 15rem;
  text-align: center;
}

.comparisonContainer {
  display: flex;
  flex: 1 1 0;
}

.graphLabelContainer {
  align-items: center;
  padding: 0 var(--jc-spacer-medium);
}

.graphLabel {
  display: block;
  margin: 0 var(--jc-spacer-large);
  text-align: center;
  width: 100%;
}
</style>
